// One hit rule per kind of drawing object (docs/claude/selection.md, *One hit
// rule per kind*): does a press at a world point land on the object? DOM-free and
// unit-tested. main.ts lists a kind's objects, leaves the hidden layers out and
// puts what is drawn on top first (`objectsAt`); the pick stack orders the kinds.
// A connection (its hit names the leg it lands on: connectionsAt) and a schedule
// table (reached through its cells: tableReachable) are picked by queries of
// their own, so they are null here.
import type {
  Polygon,
  ProductInstance,
  SectionLine,
  Vec2,
  Wall,
} from '../shared/types';
import type { KindObjects } from './drawingObjects';
import {
  dimensionGeometry,
  distance,
  distToSegment,
  drawsHeaterBlock,
  heaterGuard,
  heaterOuterHalfExtents,
  partOuterCorners,
  pointInPolygon,
  ribbonContains,
  toInstanceLocal,
} from './geometry';
import type { ItemKind } from './layers';
import { rcpOutline } from './rcp';
import {
  sectionDistance,
  sectionViewContains,
} from './section';
import type { SectionPipe } from './section';
import { textBoxContains } from './textBox';
import { wallBand } from './wallTool';
import { partHeight } from './heights';
import {
  isTilted,
  tiltedExtents,
} from './tilt';
import { isPolyRoom } from './polyRoom';

/** What a rule may ask beyond the object and the press: the view, and what the
 *  drawing holds round the object. main.ts builds it (`hitCtx`); a test builds
 *  one by hand. */
export interface HitCtx {
  /** One screen pixel in mm (1 / the view's scale): a line-like object is caught
   *  a few pixels off its line, however far the view is zoomed. */
  px: number;
  /** The plan's view, for a rule measured on screen. */
  view: { worldToScreen(p: Vec2): Vec2 };
  /** The room a reflected ceiling plan shows, while it stands and is shown. */
  shownRoom(id: string): ProductInstance | null;
  /** What a section view cuts and shows (its extent follows them). */
  sectionSources(): { instances: ProductInstance[]; walls: Wall[]; polygons: Polygon[]; pipes: readonly SectionPipe[] };
}

/** Does a press at `p` (world mm) land on the object? */
export type HitRule<T> = (obj: T, p: Vec2, ctx: HitCtx) => boolean;

/** How near a line-like object (an arc, a cloud's edge, a leader) a press catches it, px. */
export const LINE_CATCH_PX = 12;
/** How near a dimension's drawn line a press catches it, px (the snap threshold). */
export const DIM_CATCH_PX = 12;
/** The least band a 'line'-style wall is caught in, px, however thin it is drawn. */
export const WALL_LINE_CATCH_PX = 10;
/** How near a section's cut line a press catches it, px. */
export const SECTION_LINE_CATCH_PX = 7;

/** Whether a world point is inside a polygon's filled area (outside its holes). */
export function polyContains(poly: Polygon, p: Vec2): boolean {
  if (poly.points.length < 3 || !pointInPolygon(p, poly.points)) return false;
  for (const hole of poly.holes || []) if (hole.length >= 3 && pointInPolygon(p, hole)) return false;
  return true;
}

/** A press on a section's cut line (its plan mark). */
export function onSectionLine(sec: SectionLine, p: Vec2, ctx: HitCtx): boolean {
  return sectionDistance(ctx.view, sec, ctx.view.worldToScreen(p)) <= SECTION_LINE_CATCH_PX;
}

/** A press inside a section's view (the drawing it generates, in its box). */
export function inSectionView(sec: SectionLine, p: Vec2, ctx: HitCtx): boolean {
  const sv = ctx.sectionSources();
  return sectionViewContains(ctx.view, sec, sv.instances, ctx.view.worldToScreen(p), sv.walls, sv.polygons, sv.pipes);
}

const norm360 = (x: number): number => ((x % 360) + 360) % 360;

/** Centred half-extents (mm) of an instance's selection footprint: the body for
 *  most products, but a heater's overall box (body + clearance gap + cover), so
 *  its resize handles and dimensions reflect the whole object, not just the body. */
export function instanceHalfExtents(inst: ProductInstance): { hw: number; hd: number } {
  // A tilted part: the box its tilted body fills, along its plan turn.
  if (isTilted(inst)) return tiltedExtents(inst, { w: inst.width, d: inst.depth, h: partHeight(inst) });
  if (drawsHeaterBlock(inst)) { const g = heaterGuard(inst); return heaterOuterHalfExtents(inst.width, inst.depth, g.gap, g.cover); }
  return { hw: inst.width / 2, hd: inst.depth / 2 };
}

/** How near a bench run's ribbon a press catches it, mm. */
const RUN_CATCH_MM = 10;

/** Whether a part's footprint contains the world point: a bench run's ribbon, a
 *  room's polygon, else its box. `overall` takes a heater block's whole outer
 *  edge (body, gap, cover) and a tilted part's plan box; otherwise the plain
 *  width × depth body. */
export function instanceContains(inst: ProductInstance, p: Vec2, overall: boolean): boolean {
  if (inst.nodes && inst.nodes.length >= 2) return ribbonContains(inst.nodes, inst.depth, p, RUN_CATCH_MM);
  const l = toInstanceLocal(inst, p);
  if (isPolyRoom(inst)) return pointInPolygon(l, inst.polygon!);
  if (overall && drawsHeaterBlock(inst)) {
    return pointInPolygon(l, partOuterCorners(inst));
  }
  const { hw, hd } = overall ? instanceHalfExtents(inst) : { hw: inst.width / 2, hd: inst.depth / 2 };
  return Math.abs(l.x) <= hw && Math.abs(l.y) <= hd;
}

/** Every kind's rule, or null while its own query picks it. */
export const HIT_RULES = {
  arc: (a, p, { px }) => {
    if (Math.abs(distance(p, a.center) - a.radius) > LINE_CATCH_PX * px) return false;
    const ang = norm360((Math.atan2(p.y - a.center.y, p.x - a.center.x) * 180) / Math.PI);
    const span = norm360(a.endDeg - a.startDeg);
    return norm360(ang - a.startDeg) <= span || span < 1e-6;
  },
  cloud: ({ points: pts }, p, { px }) => pts.some((q, j) => distToSegment(p, q, pts[(j + 1) % pts.length]) <= LINE_CATCH_PX * px),
  conn: null,
  dim: (d, p, { px }) => {
    // Its drawn line, at its offset — never the measured segment it stands off.
    const g = dimensionGeometry(d.a, d.b, d.offset, d.kind);
    return distToSegment(p, g ? g.a : d.a, g ? g.b : d.b) <= DIM_CATCH_PX * px;
  },
  flooring: (f, p) => pointInPolygon(p, f.points),
  instance: (inst, p) => instanceContains(inst, p, false),
  leader: ({ points: pts }, p, { px }) =>
    pts.some((q, j) => j < pts.length - 1 && distToSegment(p, q, pts[j + 1]) <= LINE_CATCH_PX * px)
    || (pts.length > 0 && distance(p, pts[pts.length - 1]) <= 2 * LINE_CATCH_PX * px),
  polygon: (poly, p) => polyContains(poly, p),
  rcp: (v, p, ctx) => {
    const room = ctx.shownRoom(v.roomId);
    return !!room && pointInPolygon(p, rcpOutline(v, room));
  },
  section: (sec, p, ctx) => onSectionLine(sec, p, ctx) || inSectionView(sec, p, ctx),
  table: null,
  text: (t, p) => textBoxContains(t, p),
  wall: (w, p, { px }) => {
    const band = wallBand(w.points, w.style === 'line' ? Math.max(w.thickness, WALL_LINE_CATCH_PX * px) : w.thickness, w.align);
    return band.length >= 3 && pointInPolygon(p, band);
  },
} satisfies { [K in ItemKind]: HitRule<KindObjects[K]> | null };

/** A kind with a hit rule of its own here. */
export type HitKind = { [K in ItemKind]: (typeof HIT_RULES)[K] extends null ? never : K }[ItemKind];

/** The objects of a kind that a press at `p` lands on, in their stored order. */
export function hitsOf<K extends HitKind>(kind: K, objs: readonly KindObjects[K][], p: Vec2, ctx: HitCtx): KindObjects[K][] {
  const rule = HIT_RULES[kind] as HitRule<KindObjects[K]>;
  return objs.filter((o) => rule(o, p, ctx));
}
